import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Download, 
  Share2, 
  CheckCircle, 
  Award, 
  Home, 
  Loader2,
  Copy,
  ExternalLink,
  Linkedin
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CertificateViewPage = () => {
  const { certificateId } = useParams();
  const navigate = useNavigate();
  const { token, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchCertificate();
  }, [isAuthenticated, certificateId]);

  const fetchCertificate = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/certificates/view/${certificateId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setCertificate(data.certificate);
      } else {
        toast({ 
          title: 'Error', 
          description: 'Certificate not found', 
          variant: 'destructive' 
        });
        navigate('/my-certifications');
      }
    } catch (error) {
      console.error('Error fetching certificate:', error);
      toast({ 
        title: 'Error', 
        description: 'Failed to load certificate', 
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    window.open(`${BACKEND_URL}/api/certificates/download/${certificateId}`, '_blank');
    toast({
      title: 'Download Started',
      description: 'Your certificate is being downloaded',
    });
  };

  const handleShareToLinkedIn = async () => {
    setSharing(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/certificates/share/linkedin`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            certificate_id: certificateId
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Open LinkedIn share URL in new window
        window.open(data.linkedin_share_url, '_blank', 'width=600,height=600');
        toast({
          title: 'LinkedIn Share',
          description: 'Opening LinkedIn to share your certificate',
        });
      } else {
        throw new Error('Failed to generate share link');
      }
    } catch (error) {
      console.error('Error sharing to LinkedIn:', error);
      toast({
        title: 'Error',
        description: 'Failed to share to LinkedIn',
        variant: 'destructive'
      });
    } finally {
      setSharing(false);
    }
  };

  const copyVerificationURL = () => {
    if (certificate?.verification_url) {
      navigator.clipboard.writeText(certificate.verification_url);
      toast({
        title: 'Copied!',
        description: 'Verification URL copied to clipboard',
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!certificate) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-slate-600">Certificate not found</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const issuedDate = certificate.issued_at 
    ? new Date(certificate.issued_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : 'N/A';

  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="max-w-5xl mx-auto px-6 space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="flex items-center justify-center gap-3 mb-3">
            <Award className="w-12 h-12 text-blue-600" />
            <h1 className="text-4xl font-bold text-slate-900">Your Certificate</h1>
          </div>
          <p className="text-lg text-slate-600">
            Congratulations on earning your {certificate.certification_level} TA Certification!
          </p>
        </div>

        {/* Certificate Preview Card */}
        <Card className="border-2 border-blue-500 shadow-xl">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b-2 border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl text-blue-900">
                  {certificate.certification_level.toUpperCase()} TA Professional
                </CardTitle>
                <CardDescription className="text-blue-700 text-base mt-1">
                  RecruiterIQ Certification Program
                </CardDescription>
              </div>
              <Badge className="bg-green-100 text-green-700 border-green-300 px-4 py-2 text-base">
                <CheckCircle className="w-4 h-4 mr-2" />
                Certified
              </Badge>
            </div>
          </CardHeader>
          
          <CardContent className="p-8">
            {/* Certificate Details Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="space-y-2">
                <p className="text-sm text-slate-500 uppercase tracking-wider">Recipient</p>
                <p className="text-xl font-bold text-slate-900">{certificate.user_name}</p>
              </div>
              
              <div className="space-y-2">
                <p className="text-sm text-slate-500 uppercase tracking-wider">Badge Number</p>
                <p className="text-lg font-mono font-semibold text-blue-600 break-all">
                  {certificate.badge_number}
                </p>
              </div>
              
              <div className="space-y-2">
                <p className="text-sm text-slate-500 uppercase tracking-wider">Date Issued</p>
                <p className="text-lg font-semibold text-slate-900">{issuedDate}</p>
              </div>
              
              <div className="space-y-2">
                <p className="text-sm text-slate-500 uppercase tracking-wider">Exam Score</p>
                <p className="text-lg font-semibold text-green-600">{certificate.exam_score.toFixed(1)}%</p>
              </div>
              
              <div className="space-y-2">
                <p className="text-sm text-slate-500 uppercase tracking-wider">Certificate ID</p>
                <p className="text-sm font-mono text-slate-700">{certificate.certificate_id}</p>
              </div>
              
              <div className="space-y-2">
                <p className="text-sm text-slate-500 uppercase tracking-wider">Valid Until</p>
                <p className="text-lg font-semibold text-slate-900">{certificate.valid_until || 'Lifetime'}</p>
              </div>
            </div>

            {/* Verification Section */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                <div className="flex-1">
                  <p className="font-semibold text-blue-900 mb-1">Verification URL</p>
                  <p className="text-sm text-blue-700 mb-3">
                    Anyone can verify this certificate's authenticity using this URL:
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-white px-3 py-2 rounded border border-blue-300 text-sm text-blue-800 break-all">
                      {certificate.verification_url}
                    </code>
                    <Button 
                      onClick={copyVerificationURL} 
                      size="sm" 
                      variant="outline"
                      className="border-blue-300 hover:bg-blue-100 flex-shrink-0"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button 
                onClick={handleDownload}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                size="lg"
              >
                <Download className="w-5 h-5 mr-2" />
                Download Certificate
              </Button>
              
              <Button 
                onClick={handleShareToLinkedIn}
                disabled={sharing}
                className="flex-1 bg-[#0077b5] hover:bg-[#006399] text-white"
                size="lg"
              >
                {sharing ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <Linkedin className="w-5 h-5 mr-2" />
                )}
                Share on LinkedIn
              </Button>
            </div>

            {/* Additional Share Options */}
            <div className="mt-6 pt-6 border-t border-slate-200">
              <p className="text-sm text-slate-600 mb-3">
                💡 <strong>LinkedIn Profile Tip:</strong> You can also add this certification directly to your 
                LinkedIn profile under "Licenses & Certifications" section. Use the verification URL above to 
                link to your certificate.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* What's Next Card */}
        <Card className="border-l-4 border-l-green-500">
          <CardHeader>
            <CardTitle className="text-green-900">🎉 Congratulations!</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-700 mb-4">
              You've successfully earned your Bronze TA Professional certification. Here's what you can do next:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
              <li>Share your achievement on LinkedIn and other social media platforms</li>
              <li>Add the certificate to your resume and professional portfolio</li>
              <li>Explore Silver level certification to continue your professional development</li>
              <li>Use your new skills to advance your talent acquisition career</li>
            </ul>
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-center gap-4">
          <Button 
            onClick={() => navigate('/my-certifications')} 
            variant="outline"
            className="border-blue-300 hover:bg-blue-50"
          >
            <Award className="w-4 h-4 mr-2" />
            My Certifications
          </Button>
          <Button onClick={() => navigate('/')} variant="outline">
            <Home className="w-4 h-4 mr-2" />
            Home
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CertificateViewPage;
